import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  Box,
  DialogTitle,
  DialogContent,
  Grid,
  FormLabel,
  IconButton,
  FormControl,
  Input,
  DialogActions,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { UserDetails } from "../../types/types";

export interface AddUserListsProps {
  openDialog: boolean;
  onCloseCallbackDialog: () => void;
  onCloseRequestDialog: () => void;
  userDetails: UserDetails | null;
}

const RequestUserDialog: React.FC<AddUserListsProps> = ({
  openDialog,
  onCloseCallbackDialog,
  onCloseRequestDialog,
  userDetails,
}) => {
  const { t } = useTranslation();

  // Define state with explicit types
  const [formValues, setFormValues] = useState({
    email: "",
    phoneNumber: "",
  });

  const requiredFieldsFilled =
    formValues.email.trim() && formValues.phoneNumber.trim();

  // Assume editData is an object containing values when editing
  useEffect(() => {
    if (userDetails) {
      setFormValues({
        phoneNumber: userDetails.phone || "",
        email: userDetails.emailId || "",
      });
    }
  }, [userDetails]); // Runs when editData changes

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prevValues) => ({
      ...prevValues,
      [name]: value,
    }));
  };

  const dialogClose = () => {
    onCloseCallbackDialog();
  };

  const handleDialogSubmit = () => {
    toast.success(t("callback_request_submitted"));
    onCloseCallbackDialog();
    onCloseRequestDialog();
  };

  //   const handleDialogSubmit = async () => {
  //     const method = dialogMode === "add" ? "post" : "put";
  //     const url =
  //       API_BASE_URL +
  //       (dialogMode === "add" ? "users/adduser" : "users/update-user"); // Adjust the update endpoint if necessary

  //     const payload =
  //       dialogMode === "add"
  //         ? {
  //             webSiteId: 1,
  //             firstName: formValues.firstName,
  //             lastName: formValues.lastName,
  //             phoneNumber: Number(formValues.phoneNumber),
  //             classId: formValues.classId,
  //             locationId: formValues.businessentityLocationId,
  //             emailId: formValues.email,
  //             username: formValues.username,
  //             password: formValues.password,
  //             accountAccessType: formValues.userAccessId,
  //             isEmailVerified: formValues.emailVerified ? 1 : 0,
  //           }
  //         : {
  //             userId: editData?.id,
  //             firstName: formValues.firstName,
  //             lastName: formValues.lastName,
  //             phoneNumber: Number(formValues.phoneNumber),
  //             classId: formValues.locationClass,
  //             locationId: formValues.businessentityLocationId,
  //             emailId: formValues.email,
  //             userName: formValues.username,
  //             accountAccessType: formValues.userAccessId,
  //             isEmailVerified: formValues.emailVerified ? 1 : 0,
  //           };
  //     try {
  //       const response = await axiosInstance[method](url, payload);
  //       if (response.status >= 200 && response.status <= 300) {
  //         toast.success(response.data.data, {
  //           autoClose: 2000,
  //           position: "top-right",
  //           hideProgressBar: false,
  //           closeOnClick: true,
  //           pauseOnHover: true,
  //           draggable: true,
  //           progress: undefined,
  //         });
  //         setStatus("true");
  //         setFormValues(initialFormValues);
  //       }
  //     } catch (error) {
  //       const errCode = error as AxiosError<{ status: number }>;
  //       if (errCode.response && errCode.response.status === 401) {
  //         setIsRestrictionOpen(true);
  //       } else {
  //         toast.error("Something went wrong", {
  //           position: "top-right",
  //           autoClose: 1000,
  //           hideProgressBar: false,
  //           closeOnClick: false,
  //           pauseOnHover: false,
  //           draggable: false,
  //           progress: undefined,
  //         });
  //       }
  //       throw error;
  //     }
  //     onCloseDialog();
  //   };

  return (
    <Box sx={{ display: "block", margin: "20px" }}>
      <Dialog open={openDialog} onClose={dialogClose} fullWidth maxWidth="md">
        <DialogTitle className="dialogtitle">
          {/* {t("add_user")} */}
          Let’s Build Your Perfect Plan
          <IconButton
            edge="end"
            color="inherit"
            onClick={dialogClose}
            aria-label="close"
            sx={{
              margin: "auto",
              position: "absolute",
              right: "25px",
              top: "17px",

              width: "30px",
              height: "30px",
              borderRadius: "50%",
              border: "1px solid #be1e2d", // Red border
              color: "#be1e2d", // Default text color
              "&:hover": {
                backgroundColor: "#be1e2d", // Full red background on hover
                color: "#ffffff", // White icon color
              },
            }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ mt: 2 }}>
            We’ll create a custom pricing plan that fits your team’s unique
            needs. Our support team will contact you shortly to discuss the best
            solution. <br />
            You can also update your preferred contact details below if
            needed—we’re flexible and here to help!
          </Typography>
          <Grid container spacing={3} sx={{ padding: 2 }} className="addUser">
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel className="addUser_formlabel" required>
                  {t("email")}
                </FormLabel>
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Input
                    name="email"
                    size="small"
                    value={formValues.email}
                    onChange={handleInputChange}
                    fullWidth
                    className="addUser_input"
                    sx={{
                      flex: 9,
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "5px",
                    }} // Adjust border and padding as needed
                  />
                </Box>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel className="addUser_formlabel" required>
                  {t("phone_number")}
                </FormLabel>
                <Box
                  sx={{ display: "flex", alignItems: "center", width: "100%" }}
                >
                  <Input
                    name="phoneNumber"
                    size="small"
                    value={formValues.phoneNumber}
                    onChange={handleInputChange}
                    fullWidth
                    className="addUser_input"
                  />
                </Box>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ borderTop: "1px solid #ccc" }}>
          <Button onClick={dialogClose} className="cancelbtn">
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDialogSubmit}
            variant="contained"
            color="primary"
            className="addbtn"
            sx={{
              cursor: !requiredFieldsFilled ? "not-allowed" : "pointer",
              opacity: !requiredFieldsFilled ? 0.9 : 1,
              pointerEvents: !requiredFieldsFilled ? "none" : "auto",
            }}
            disabled={!requiredFieldsFilled}
          >
            {t("send")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestUserDialog;
